import React, { useState, useEffect } from 'react';
import {
  MessageSquare, TrendingUp, Bot,
  ThumbsUp, ThumbsDown, Minus, AlertCircle,
  Users, Hourglass, UserCheck, ArrowUpRight, CheckCircle2, XCircle,
  Moon, Stethoscope, Activity
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { useAuth, API } from '../../App';
import { Alert, AlertDescription } from '../../components/ui/alert';
import axios from 'axios';

import ChannelSelector from '../../components/ChannelSelector';

const AnalyticsPage = () => {
  const { token, user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [messageVolume, setMessageVolume] = useState([]);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [chartError, setChartError] = useState(null);
  const [selectedChannelId, setSelectedChannelId] = useState(null);

  const isOperator = user?.role === 'operator' && !user?.is_super_admin;
  const isOwnerOrAdmin = user?.role === 'owner' || user?.role === 'admin' || user?.is_super_admin;

  useEffect(() => {
    if (selectedChannelId) {
      setLoading(true);
      fetchAnalytics();
      fetchMessageVolume();
      fetchInsights();
    }
  }, [selectedChannelId]);

  const fetchAnalytics = async () => {
    try {
      const params = selectedChannelId && selectedChannelId !== 'all' 
        ? { channel_id: selectedChannelId } 
        : {};
      const response = await axios.get(`${API}/analytics`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInsights = async () => {
    try {
      const params = selectedChannelId && selectedChannelId !== 'all'
        ? { channel_id: selectedChannelId }
        : {};
      const response = await axios.get(`${API}/analytics/insights`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setInsights(response.data);
    } catch (error) {
      console.error('Failed to fetch insights:', error);
      // Soft-fail — leave insights null; widgets will show placeholders.
    }
  };

  const fetchMessageVolume = async () => {
    try {
      const params = selectedChannelId && selectedChannelId !== 'all'
        ? { channel_id: selectedChannelId }
        : {};
      const response = await axios.get(`${API}/analytics/message-volume`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setMessageVolume(response.data);
      setChartError(null);
    } catch (error) {
      console.error('Failed to fetch message volume:', error);
      setChartError('Failed to load chart data');
    }
  };

  // Check if there's no data
  const hasNoData = !analytics || (analytics.total_conversations === 0 && analytics.total_messages === 0);
  const hasChartData = messageVolume.some(d => d.count > 0);
  const maxCount = Math.max(...messageVolume.map(d => d.count), 1);

  const stats = [
    {
      title: isOperator ? 'My Conversations' : 'Total Conversations',
      value: analytics?.total_conversations || 0,
      icon: MessageSquare,
      description: isOperator ? 'Assigned to you' : 'Organization total'
    },
    {
      title: isOperator ? 'Messages Handled' : 'Total Messages',
      value: analytics?.total_messages || 0,
      icon: MessageSquare,
      description: isOperator ? 'In your conversations' : 'All messages'
    },
    {
      title: 'AI Handled',
      value: `${analytics?.ai_handled_percentage || 0}%`,
      icon: Bot,
      description: 'AI-assisted responses'
    }
  ];

  // Use real sentiment data from API
  const totalSentiment = (analytics?.sentiment_distribution?.positive || 0) + 
                         (analytics?.sentiment_distribution?.neutral || 0) + 
                         (analytics?.sentiment_distribution?.negative || 0);
  
  const sentimentData = totalSentiment > 0 ? [
    { 
      label: 'Positive', 
      value: Math.round((analytics?.sentiment_distribution?.positive || 0) / totalSentiment * 100), 
      count: analytics?.sentiment_distribution?.positive || 0,
      icon: ThumbsUp, 
      color: 'text-emerald-500' 
    },
    { 
      label: 'Neutral', 
      value: Math.round((analytics?.sentiment_distribution?.neutral || 0) / totalSentiment * 100), 
      count: analytics?.sentiment_distribution?.neutral || 0,
      icon: Minus, 
      color: 'text-slate-500' 
    },
    { 
      label: 'Negative', 
      value: Math.round((analytics?.sentiment_distribution?.negative || 0) / totalSentiment * 100), 
      count: analytics?.sentiment_distribution?.negative || 0,
      icon: ThumbsDown, 
      color: 'text-red-500' 
    }
  ] : [
    { label: 'Positive', value: 0, count: 0, icon: ThumbsUp, color: 'text-emerald-500' },
    { label: 'Neutral', value: 0, count: 0, icon: Minus, color: 'text-slate-500' },
    { label: 'Negative', value: 0, count: 0, icon: ThumbsDown, color: 'text-red-500' }
  ];

  // Use real intent data from API
  const topIntents = analytics?.top_intents?.length > 0 
    ? analytics.top_intents.map((intent, i) => ({
        name: intent.name.charAt(0).toUpperCase() + intent.name.slice(1),
        count: intent.count,
        percentage: analytics.total_conversations > 0 
          ? Math.round(intent.count / analytics.total_conversations * 100) 
          : 0
      }))
    : [];

  // Format date for display (e.g., "Mon", "Tue")
  const formatDateLabel = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  };

  return (
    <div className="space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">
          {isOperator ? 'My Analytics' : 'Analytics Dashboard'}
        </h1>
        <p className="text-muted-foreground">
          {isOperator 
            ? 'Track your personal performance metrics' 
            : 'Monitor your conversation metrics and team performance'}
        </p>
        {isOwnerOrAdmin && (
          <div className="mt-2" data-testid="analytics-channel-selector">
            <ChannelSelector
              value={selectedChannelId}
              onChange={(channelId) => setSelectedChannelId(channelId)}
              showAllOption={user?.role === 'owner' || user?.is_super_admin}
            />
          </div>
        )}
      </div>

      {/* Role-based info banner */}
      {isOperator && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            You&apos;re viewing analytics for conversations assigned to you. Contact your admin for organization-wide metrics.
          </AlertDescription>
        </Alert>
      )}

      {/* Empty state for no data */}
      {hasNoData && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <TrendingUp className="w-16 h-16 text-muted-foreground opacity-30 mb-4" />
            <h3 className="text-lg font-medium text-muted-foreground">
              {isOperator ? 'No conversations assigned yet' : 'No data available'}
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {isOperator 
                ? 'Analytics will appear here once you have assigned conversations'
                : 'Analytics will appear here once you receive messages'}
            </p>
          </CardContent>
        </Card>
      )}
      
      {/* Stats Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {stats.map((stat, i) => (
          <Card key={i} data-testid={`stat-card-${i}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                  <stat.icon className="w-6 h-6 text-emerald-700 dark:text-emerald-400" />
                </div>
              </div>
              <div className="mt-4">
                <p className="text-2xl font-bold">{stat.value}</p>
                <p className="text-sm text-muted-foreground">{stat.title}</p>
                <p className="text-xs text-muted-foreground/70 mt-1">{stat.description}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      
      {/* Message Volume Chart */}
      <Card data-testid="message-volume-chart">
        <CardHeader>
          <CardTitle>Message Volume (Last 7 Days)</CardTitle>
          <CardDescription>
            {isOperator ? 'Messages in your conversations' : 'Organization message activity'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {chartError ? (
            <div className="h-64 flex items-center justify-center text-muted-foreground">
              <div className="text-center">
                <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-30 text-red-500" />
                <p className="text-sm text-red-500">{chartError}</p>
                <button 
                  onClick={fetchMessageVolume}
                  className="text-xs text-emerald-600 hover:underline mt-2"
                >
                  Retry
                </button>
              </div>
            </div>
          ) : !hasChartData ? (
            <div className="h-64 flex items-center justify-center text-muted-foreground">
              <div className="text-center">
                <TrendingUp className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p className="text-sm">No message data yet</p>
                <p className="text-xs mt-1">Messages will appear here as they are received</p>
              </div>
            </div>
          ) : (
            <div className="h-64">
              {/* Chart Container */}
              <div className="flex h-full items-end gap-2 pt-8 pb-6 px-2">
                {messageVolume.map((day, i) => {
                  const heightPercent = maxCount > 0 ? (day.count / maxCount) * 100 : 0;
                  return (
                    <div 
                      key={day.date} 
                      className="flex-1 flex flex-col items-center gap-2"
                      data-testid={`chart-bar-${i}`}
                    >
                      {/* Count label */}
                      <span className="text-xs font-medium text-muted-foreground">
                        {day.count > 0 ? day.count : ''}
                      </span>
                      {/* Bar */}
                      <div className="w-full flex-1 flex items-end">
                        <div 
                          className="w-full bg-emerald-500 rounded-t-md transition-all duration-500 min-h-[4px]"
                          style={{ height: `${Math.max(heightPercent, day.count > 0 ? 5 : 2)}%` }}
                        />
                      </div>
                      {/* Date label */}
                      <span className="text-xs text-muted-foreground font-medium">
                        {formatDateLabel(day.date)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Charts Row - Only show if there's data */}
      {!hasNoData && (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Sentiment Distribution */}
          <Card>
            <CardHeader>
              <CardTitle>Customer Sentiment</CardTitle>
              <CardDescription>
                {isOperator ? 'Sentiment in your conversations' : 'Overall sentiment distribution'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {totalSentiment === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No sentiment data yet</p>
                  <p className="text-sm mt-1">Sentiment is detected when AI processes messages</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {sentimentData.map((item, i) => (
                    <div key={i} className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center ${item.color}`}>
                        <item.icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium">{item.label}</span>
                          <span className="text-sm text-muted-foreground">{item.count} ({item.value}%)</span>
                        </div>
                        <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full ${
                              item.label === 'Positive' ? 'bg-emerald-500' :
                              item.label === 'Neutral' ? 'bg-slate-400' : 'bg-red-500'
                            }`}
                            style={{ width: `${item.value}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Top Intents */}
          <Card>
            <CardHeader>
              <CardTitle>Top Conversation Intents</CardTitle>
              <CardDescription>
                {isOperator ? 'Intent distribution in your conversations' : 'What customers are asking about'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {topIntents.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No intent data yet</p>
                  <p className="text-sm mt-1">Intents are detected when AI processes messages</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {topIntents.map((intent, i) => (
                    <div key={i} className="flex items-center gap-4">
                      <div className="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                        <span className="text-sm font-bold text-emerald-700 dark:text-emerald-400">{i + 1}</span>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium">{intent.name}</span>
                          <span className="text-sm text-muted-foreground">{intent.count}</span>
                        </div>
                        <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-emerald-500 rounded-full"
                            style={{ width: `${intent.percentage}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
      {/* ── Actionable Insights (Feb-2026): forward-only, populates from new conversations ── */}
      {!hasNoData && !isOperator && (
        <InsightsSection insights={insights} />
      )}
    </div>
  );
};

// ── Insights widgets (read-only — driven entirely by /analytics/insights) ──
const _PLACEHOLDER = "Waiting for more conversation data";

const InsightWaiting = ({ icon: Icon = TrendingUp }) => (
  <div className="text-center py-8 text-muted-foreground" data-testid="insight-placeholder">
    <Icon className="w-10 h-10 mx-auto mb-3 opacity-30" />
    <p className="text-sm">{_PLACEHOLDER}</p>
  </div>
);

const FUNNEL_STAGES = [
  { key: 'new_leads',        label: 'New Leads',             icon: Users,        color: 'bg-sky-500' },
  { key: 'collecting_info',  label: 'Collecting Information',icon: Hourglass,    color: 'bg-amber-500' },
  { key: 'pending_human',    label: 'Pending Human',         icon: UserCheck,    color: 'bg-purple-500' },
  { key: 'escalated',        label: 'Escalated',             icon: ArrowUpRight, color: 'bg-rose-500' },
  { key: 'booked_converted', label: 'Booked / Converted',    icon: CheckCircle2, color: 'bg-emerald-500' },
  { key: 'closed',           label: 'Closed',                icon: XCircle,      color: 'bg-slate-400' },
];

const InsightsSection = ({ insights }) => {
  const funnel = insights?.lead_funnel || {};
  const funnelTotal = FUNNEL_STAGES.reduce((s, st) => s + (funnel[st.key] || 0), 0);
  const treatments = insights?.top_treatment_interests || [];
  const afterHours = insights?.after_hours || { total: 0, percentage: 0, ai_handled: 0 };
  const escalationReasons = insights?.escalation_reasons || [];
  const dh = insights?.data_health || {};

  const showFunnel    = funnelTotal >= 1;
  const showTreatments= treatments.length >= 1;
  const showAfter     = (dh.with_created_at || 0) >= 1; // we have at least one timestamped conv
  const showReasons   = (dh.with_escalation || 0) >= 1;

  const maxStage = Math.max(1, ...FUNNEL_STAGES.map(st => funnel[st.key] || 0));

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="insights-section">
      {/* Lead Funnel */}
      <Card data-testid="insight-lead-funnel">
        <CardHeader>
          <CardTitle>Lead Funnel</CardTitle>
          <CardDescription>Where enquiries are right now</CardDescription>
        </CardHeader>
        <CardContent>
          {!showFunnel ? <InsightWaiting icon={Activity} /> : (
            <div className="space-y-3">
              {FUNNEL_STAGES.map((st) => {
                const v = funnel[st.key] || 0;
                const pct = Math.round((v / maxStage) * 100);
                const Icon = st.icon;
                return (
                  <div key={st.key} className="flex items-center gap-4" data-testid={`funnel-${st.key}`}>
                    <div className="w-9 h-9 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                      <Icon className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium">{st.label}</span>
                        <span className="text-sm text-muted-foreground">{v}</span>
                      </div>
                      <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                        <div className={`h-full ${st.color} rounded-full`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top Treatment Interests */}
      <Card data-testid="insight-treatments">
        <CardHeader>
          <CardTitle>Top Treatment Interests</CardTitle>
          <CardDescription>What patients are actually asking about</CardDescription>
        </CardHeader>
        <CardContent>
          {!showTreatments ? <InsightWaiting icon={Stethoscope} /> : (
            <div className="space-y-4">
              {treatments.map((t, i) => (
                <div key={`${t.name}-${i}`} className="flex items-center gap-4" data-testid={`treatment-${i}`}>
                  <div className="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                    <span className="text-sm font-bold text-emerald-700 dark:text-emerald-400">{i + 1}</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium">{t.name}</span>
                      <span className="text-sm text-muted-foreground">{t.count} ({t.percentage}%)</span>
                    </div>
                    <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${t.percentage}%` }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* After-Hours Enquiries */}
      <Card data-testid="insight-after-hours">
        <CardHeader>
          <CardTitle>After-Hours Enquiries</CardTitle>
          <CardDescription>Value generated when staff are unavailable</CardDescription>
        </CardHeader>
        <CardContent>
          {!showAfter ? <InsightWaiting icon={Moon} /> : afterHours.total === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Moon className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No after-hours enquiries yet</p>
              <p className="text-xs mt-1">All recent traffic arrived during business hours</p>
            </div>
          ) : (
            <div className="flex items-center gap-6 py-2">
              <div className="w-16 h-16 rounded-2xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center flex-shrink-0">
                <Moon className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div className="flex-1">
                <p className="text-4xl font-bold leading-tight" data-testid="after-hours-total">{afterHours.total}</p>
                <p className="text-sm text-muted-foreground">
                  {afterHours.percentage}% of enquiries · {afterHours.ai_handled} handled by AI
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Escalation Reasons */}
      <Card data-testid="insight-escalation-reasons">
        <CardHeader>
          <CardTitle>Escalation Reasons</CardTitle>
          <CardDescription>Why conversations needed a human</CardDescription>
        </CardHeader>
        <CardContent>
          {!showReasons ? <InsightWaiting icon={ArrowUpRight} /> : (
            <div className="space-y-3">
              {escalationReasons.map((r, i) => {
                const max = Math.max(1, ...escalationReasons.map(x => x.count));
                const pct = Math.round((r.count / max) * 100);
                return (
                  <div key={`${r.label}-${i}`} className="flex items-center gap-4" data-testid={`escalation-${i}`}>
                    <div className="w-9 h-9 rounded-lg bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center">
                      <ArrowUpRight className="w-4 h-4 text-rose-600 dark:text-rose-400" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium">{r.label}</span>
                        <span className="text-sm text-muted-foreground">{r.count}</span>
                      </div>
                      <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                        <div className="h-full bg-rose-500 rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalyticsPage;
